import React, { useContext, useEffect, useState } from "react"
import { FlowTransitions, CapitalBool } from "../../model/Flow"
import Popup from "reactjs-popup"
import { ColorPicker } from "../view/ColorPicker"
import { MachineContext } from "../fsm/useMachine"
import { SET_INDEX, MARK_ACTIVE, MARK_ALL_INACTIVE, OVERRIDE_FLOW, OVERRIDE_SCREEN } from "../fsm/types"
import {Settings as Icon, XCircle, ChevronLeft, ChevronRight, Share2} from "react-feather"
import PrivacySettings from "./PrivacySettings"
import { string_id } from "../utils/getInfo"


export const Settings = () => {
    return (
        <Popup
            trigger={
                <button className="empty" title="Open Settings Menu">
                    <Icon className="feather" />
                </button>
            }
            modal
            closeOnDocumentClick
            closeOnEscape
            className="settings"
        >
            {close => (
                <SettingsModal close={close} />
            )}
        </Popup>
    )
}

interface ModalProps {
    close: () => void
}

type SettingsState = {
    autoAdvanceSlideTransitionType?: number,
    autoAdvanceScreenSlideTransitionType?: number,
    autoAdvanceLoops: CapitalBool,
    slideGestureForNavigationDisabled: boolean,
    autoAdvanceSlideDuration?: number,
    autoAdvanceScreenSlideDuration?: number
};

export const SettingsModal: React.FC<ModalProps> = ({ close }) => {
    const [current, send] = useContext(MachineContext)
    const { flow, index } = current.context
    const screen = flow.screens[index]
    const loopRef = React.useRef<(HTMLInputElement | null)>(null)
    const [activeTab, setActiveTab] = useState<"flow" | "privacy">("flow")
    const [state, setState] = React.useState<SettingsState>({
        autoAdvanceSlideTransitionType: flow.autoAdvanceSlideTransitionType,
        autoAdvanceScreenSlideTransitionType: flow.screens[index].autoAdvanceSlideTransitionType,
        autoAdvanceLoops: flow.autoAdvanceLoops,
        slideGestureForNavigationDisabled: flow.screens[index].slideGestureForNavigationDisabled,
        autoAdvanceSlideDuration: flow.autoAdvanceSlideDuration,
        autoAdvanceScreenSlideDuration: flow.screens[index].autoAdvanceSlideDuration
    })

    // autoAdvanceSlideTransitionType: number
    // autoAdvanceLoops: "YES" | "NO"
    // slideGestureForNavigationDisabled: boolean
    // autoAdvanceSlideDuration: number

    const submit = () => {
        flow.autoAdvanceLoops = state.autoAdvanceLoops
        flow.autoAdvanceSlideDuration = state.autoAdvanceSlideDuration
        flow.autoAdvanceSlideTransitionType = state.autoAdvanceSlideTransitionType
        flow.screens[index].slideGestureForNavigationDisabled = state.slideGestureForNavigationDisabled
        flow.screens[index].autoAdvanceSlideDuration = state.autoAdvanceScreenSlideDuration
        flow.screens[index].autoAdvanceSlideTransitionType = state.autoAdvanceScreenSlideTransitionType
        send({ type: OVERRIDE_FLOW, flow })
    }

    const submitPresenterNotes = () => {
        send({ type: OVERRIDE_SCREEN, screen })
    }

    useEffect(() => {
        send({ type: MARK_ALL_INACTIVE })
    }, [])

    // Check if flow is published (has a string_id from the URL)
    const isPublished = string_id && string_id.length > 0;

    // If showing privacy tab, render PrivacySettings component
    if (activeTab === "privacy") {
        return <PrivacySettings close={close} />;
    }

    return (
        <div className="settings-modal-2025">
            <div className="settings-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                    <h2>Flow Settings</h2>
                    {isPublished && (
                        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', marginRight: '40px' }}>
                            <button
                                onClick={() => setActiveTab("flow")}
                                className={`settings-tab-button ${activeTab === "flow" ? 'active' : ''}`}
                            >
                                Presentation
                            </button>
                            <button
                                onClick={() => setActiveTab("privacy")}
                                className={`settings-tab-button ${activeTab === "privacy" ? 'active' : ''}`}
                            >
                                <Share2 size={14} />
                                Privacy & Sharing
                            </button>
                        </div>
                    )}
                </div>
                <XCircle className="feather close-icon" onClick={() => {
                    submit()
                    close()
                }} style={{ cursor: 'pointer' }} />
            </div>

            <div className="settings-content">
                {/* Global Settings Section */}
                <section className="settings-section">
                    <h3>Global Settings</h3>
                    <div className="settings-card">
                        <div className="setting-group">
                            <div className="setting-row">
                                <label htmlFor="auto-advance" className="setting-label">
                                    <input
                                        type="checkbox"
                                        id="auto-advance"
                                        defaultChecked={typeof state.autoAdvanceSlideDuration != "undefined"}
                                        onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                setState(x => ({
                                                    ...x,
                                                    autoAdvanceSlideTransitionType: 0,
                                                    autoAdvanceSlideDuration: 10.0,
                                                }))
                                            } else {
                                                setState(x => ({
                                                    ...x,
                                                    autoAdvanceSlideTransitionType: undefined,
                                                    autoAdvanceSlideDuration: undefined,
                                                }))
                                            }
                                        }}
                                    />
                                    <span>Auto-Advance</span>
                                </label>
                            </div>

                            <div className="setting-row">
                                <label htmlFor="loop" className="setting-label">
                                    <input
                                        type="checkbox"
                                        id="loop"
                                        ref={loopRef}
                                        defaultChecked={state.autoAdvanceLoops === "YES"}
                                        onChange={() => setState(x => ({
                                            ...x,
                                            autoAdvanceLoops: x.autoAdvanceLoops === "YES" ? "NO" : "YES",
                                            autoAdvanceSlideDuration: 10.0,
                                            autoAdvanceSlideTransitionType: 0,
                                        }))}
                                        disabled={typeof state.autoAdvanceSlideDuration == "undefined"}
                                    />
                                    <span>Loop</span>
                                </label>
                            </div>

                            <div className="setting-row">
                                <label htmlFor="Duration" className="setting-label-inline">Duration (seconds)</label>
                                <input
                                    className="setting-input"
                                    type="text"
                                    id="Duration"
                                    defaultValue={state.autoAdvanceSlideDuration || "10.0"}
                                    onChange={e => {
                                        let val = parseFloat(e.currentTarget.value)
                                        setState(x => ({
                                            ...x,
                                            autoAdvanceSlideDuration: val,
                                            autoAdvanceSlideTransitionType: x.autoAdvanceSlideTransitionType || 0
                                        }))
                                    }}
                                    disabled={typeof state.autoAdvanceSlideDuration == "undefined"}
                                />
                            </div>

                            <div className="setting-row">
                                <label className="setting-label-inline">Transition</label>
                                <FlowSelector
                                    autoAdvanceSlideTransitionType={state.autoAdvanceSlideTransitionType || 0}
                                    changeHandler={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        let val = e.currentTarget.selectedIndex
                                        setState(x => ({ ...x, autoAdvanceSlideTransitionType: val }))
                                    }}
                                    disabled={typeof state.autoAdvanceSlideDuration == "undefined"}
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Screen Settings Section */}
                <section className="settings-section">
                    <h3>Screen Settings</h3>
                    <div className="settings-card">
                        <div className="setting-group">
                            <div className="setting-row">
                                <label htmlFor="override" className="setting-label">
                                    <input
                                        type="checkbox"
                                        id="override"
                                        defaultChecked={typeof state.autoAdvanceScreenSlideDuration != "undefined"}
                                        onChange={(e) => {
                                            if (e.currentTarget.checked) {
                                                setState(x => ({
                                                    ...x,
                                                    autoAdvanceScreenSlideDuration: 0.0,
                                                    autoAdvanceScreenSlideTransitionType: x.autoAdvanceScreenSlideTransitionType || 0
                                                }))
                                            } else {
                                                setState(x => ({
                                                    ...x,
                                                    autoAdvanceScreenSlideTransitionType: undefined,
                                                    autoAdvanceScreenSlideDuration: undefined
                                                }))
                                            }
                                        }}
                                    />
                                    <span>Override Global Autoplay</span>
                                </label>
                            </div>

                            <div className="setting-row">
                                <label className="setting-label-inline">Duration (seconds)</label>
                                <input
                                    className="setting-input"
                                    type="text"
                                    name="Duration"
                                    defaultValue={state.autoAdvanceScreenSlideDuration || "0"}
                                    onChange={e => {
                                        let val = parseFloat(e.target.value)
                                        setState(x => ({ ...x, autoAdvanceScreenSlideDuration: val }))
                                    }}
                                    disabled={typeof state.autoAdvanceScreenSlideDuration == "undefined"}
                                />
                            </div>

                            <div className="setting-row">
                                <label className="setting-label-inline">Transition</label>
                                <FlowSelector
                                    autoAdvanceSlideTransitionType={screen.autoAdvanceSlideTransitionType || 0}
                                    changeHandler={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        let val = e.currentTarget.selectedIndex
                                        setState(x => ({ ...x, autoAdvanceScreenSlideTransitionType: val }))
                                    }}
                                    disabled={typeof state.autoAdvanceScreenSlideDuration == "undefined"}
                                />
                            </div>

                            <div className="setting-row">
                                <label htmlFor="disable-gesture" className="setting-label">
                                    <input
                                        type="checkbox"
                                        id="disable-gesture"
                                        defaultChecked={state.slideGestureForNavigationDisabled}
                                        onChange={() => setState(x => ({ ...x, slideGestureForNavigationDisabled: !x.slideGestureForNavigationDisabled }))}
                                    />
                                    <span>Disable Slide Gesture</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Background Color Section */}
                <section className="settings-section">
                    <h3>Background Color</h3>
                    <div className="settings-card">
                        <div className="setting-row">
                            <ColorPicker />
                        </div>
                    </div>
                </section>

                {/* Presenter Notes Section */}
                <section className="settings-section">
                    <h3>Presenter Notes</h3>
                    <div className="settings-card">
                        <textarea
                            className="settings-textarea"
                            defaultValue={flow.screens[index].presenterNotes}
                            placeholder="Add notes for this screen..."
                            onChange={e => {
                                send({ type: MARK_ACTIVE, id: "presenterNotes" })
                                e.preventDefault()
                                e.stopPropagation()
                                screen.setNotes(e.currentTarget.value)
                                submitPresenterNotes()
                            }}
                        />
                    </div>
                </section>
            </div>

            {/* Footer with screen navigation */}
            <div className="settings-footer">
                <div className="screen-navigation">
                    <button
                        className="nav-button"
                        onClick={() => send({ type: SET_INDEX, index: index - 1 })}
                        disabled={index === 0}
                    >
                        <ChevronLeft className="feather" size={18} />
                    </button>
                    <span className="screen-indicator">Screen {index + 1} of {flow.screens.length}</span>
                    <button
                        className="nav-button"
                        onClick={() => send({ type: SET_INDEX, index: index + 1 })}
                        disabled={index === flow.screens.length - 1}
                    >
                        <ChevronRight className="feather" size={18} />
                    </button>
                </div>
                <button
                    className="done-button"
                    onClick={e => {
                        e.stopPropagation()
                        submit()
                        close()
                    }}
                >
                    Done
                </button>
            </div>
        </div>
    )
}

interface SelectorProps {
    autoAdvanceSlideTransitionType: number
    changeHandler(e: React.ChangeEvent<HTMLSelectElement>): void
    disabled: boolean
}

export const FlowSelector: React.FC<SelectorProps> = ({
    autoAdvanceSlideTransitionType,
    changeHandler,
    disabled,
}) => (
        <select
            defaultValue={String(autoAdvanceSlideTransitionType)}
            onChange={changeHandler}
            disabled={disabled}
        >
            {FlowTransitions.map((val, idx) => {
                return (
                    <option value={idx} key={idx}>
                        {val}
                    </option>
                )
            })}
        </select>
    )

Settings.displayName = "Settings"
export default Settings
